import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";

// Same-origin base so the companion app works on both the production domain
// (cresttorconstruction.com) and the preview domain without cross-site issues.
const BACKEND_URL =
  (typeof window !== "undefined" && window.location?.origin) ||
  process.env.REACT_APP_BACKEND_URL ||
  "";
export const COMPANION_API = `${BACKEND_URL}/api/companion`;

const companionApi = axios.create({ baseURL: COMPANION_API });
companionApi.interceptors.request.use((config) => {
  const t = localStorage.getItem("crest_companion_token");
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

export const companion = companionApi;

const CompanionAuthContext = createContext(null);

export const CompanionAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [ready, setReady] = useState(false);

  const refresh = useCallback(async () => {
    const token = localStorage.getItem("crest_companion_token");
    if (!token) {
      setUser(false);
      setReady(true);
      return;
    }
    try {
      const { data } = await companionApi.get("/me");
      setUser(data);
    } catch {
      localStorage.removeItem("crest_companion_token");
      setUser(false);
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = useCallback(async (email, password) => {
    const { data } = await companionApi.post("/auth/login", { email, password });
    localStorage.setItem("crest_companion_token", data.token);
    setUser({ id: data.id, email: data.email, name: data.name, role: data.role, phone: data.phone });
    return data;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("crest_companion_token");
    setUser(false);
  }, []);

  const value = useMemo(() => ({ user, ready, login, logout, refresh }),
    [user, ready, login, logout, refresh]);

  return <CompanionAuthContext.Provider value={value}>{children}</CompanionAuthContext.Provider>;
};

export const useCompanionAuth = () => useContext(CompanionAuthContext);

export const companionFileUrl = (relative) => {
  if (!relative) return "";
  if (relative.startsWith("http")) return relative;
  return `${BACKEND_URL}${relative}`;
};
